import React, { useEffect, useState } from 'react';

import {
  Box,
  CardContent,
  Grid,
  Typography,
  Link,
  Card,
  Slider,
  Skeleton,
  Badge,
  Chip,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { COLOR_PRIMARY, COLOR_TOOLTIP_BACKGROUND, exchangeRates } from 'src/utils/const';
import { CustomIconButton, InfoButton } from 'src/App/Dashboards/Market/Components/Buttons';
import { formatCurrency } from 'src/utils/functions';
import { reportPageStyles as styles } from '../styles';
import {
  COLOR_GREY_200,
  COLOR_GREY_600,
  COLOR_GREY_800,
  COLOR_TEXT_1,
  COLOR_TEXT_4,
} from 'src/utils/colors';
import EditButton from 'src/App/Dashboards/Market/Components/Buttons/EditButton';
import { InputTextField } from 'src/App/Dashboards/Market/Components/Inputs';
import TooltipContent from './CalculatorKPITooltip';

const KPICard = ({
  kpiKey,
  kpiName,
  value,
  tooltipConfig,
  unitType,
  currency,
  isUserReportOwner,
  isEditable,
  editMode,
  handleEditModalOpen,
  valuePercentiles,
  hanldeKpiEdit,
  selectedCompsCount,
  ltmDateRange,
  isDisabled,
  showEditedBadge,
  editedMetrics,
}: {
  kpiKey: string;
  kpiName: string;
  value: number | number[] | 'loading' | null;
  tooltipConfig: {
    name: string;
    timePeriod: string;
    source: string;
    formula?: string;
    definition: string[];
    ctaText: string;
    ctaURL: string;
    helpingUrls?: {
      text: string;
      ctaText: string;
      ctaURL: string;
    }[];
  } | null;
  unitType: string;
  currency: string;
  isUserReportOwner: boolean;
  isEditable: boolean;
  editMode: 'scroll' | 'modal' | null;
  handleEditModalOpen: () => void;
  valuePercentiles?: {
    25: number;
    50: number;
    75: number;
    90: number;
  };
  hanldeKpiEdit: (key: string, value: any) => void;
  selectedCompsCount: number;
  ltmDateRange: string;
  isDisabled?: boolean;
  showEditedBadge?: boolean;
  editedMetrics?: string[];
}) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down(900));

  const [inLineEditToggled, setInLineEditToggled] = useState(false);
  const inLineEditConfig = valuePercentiles
    ? {
        min: Math.round(valuePercentiles[25] * 0.9),
        max: Math.round(valuePercentiles[75] * 1.1),
        marks: [
          {
            value: valuePercentiles[25],
            label: '25th',
            isLabelHidden: false,
          },
          {
            value: valuePercentiles[50],
            label: '50th',
            isLabelHidden: false,
          },
          {
            value: valuePercentiles[75],
            label: '75th',
            isLabelHidden: false,
          },
        ],
      }
    : null;
  const [editValue, setEditValue] = useState<number>(value as number);

  useEffect(() => {
    setEditValue(value as number);
  }, [value]);

  return (
    <Grid
      container
      item
      xs={inLineEditToggled ? 12 : 6}
      justifyContent={'center'}
      alignItems={'center'}
      sx={{ padding: matches ? '3px' : '5px' }}
    >
      <Grid
        container
        item
        xs={12}
        justifyContent={'center'}
        alignContent={'center'}
        alignItems={'center'}
        sx={{
          ...styles.paper,
          height: inLineEditToggled ? '200px' : matches ? '100px' : '140px',
          padding: matches ? '12px' : '20px',
          ...(matches && isEditable && isUserReportOwner ? { cursor: 'pointer' } : {}),
        }}
        onClick={matches && isEditable && isUserReportOwner ? () => {
          if (editMode === 'modal') handleEditModalOpen();
          else if (editMode === 'scroll') setInLineEditToggled(true);
        } : undefined}
      >
        <>
          <Grid item xs={12} alignItems={'center'} sx={{ marginBottom: matches ? '4px' : '10px' }}>
            <Typography variant="body1" sx={{ fontSize: matches ? '13px' : '16px', color: COLOR_TEXT_4 }}>
              {kpiName}{' '}
              {tooltipConfig && (
                <InfoButton
                  tooltipContent={
                    <TooltipContent
                      tooltipConfig={tooltipConfig}
                      selectedCompsCount={selectedCompsCount}
                      ltmDateRange={ltmDateRange}
                    />
                  }
                  iconColor={COLOR_TEXT_4}
                />
              )}
            </Typography>
          </Grid>
          {!inLineEditToggled ? (
            <Grid
              item
              xs={12}
              container
              direction={'row'}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              {value == 'loading' ? (
                <Skeleton
                  variant="text"
                  sx={{ fontSize: '5rem', borderRadius: 4, width: '100%' }}
                />
              ) : value != null ? (
                <Typography
                  variant={matches ? 'body1' : 'h5'}
                  sx={{
                    fontWeight: 'bold',
                    color: COLOR_TEXT_1,
                    filter: isDisabled ? 'blur(7px)' : 'none',
                    ...(matches ? { fontSize: '16px' } : {}),
                  }}
                >
                  {unitType === '$' && exchangeRates[currency].symbol}
                  {unitType === '$' && typeof value == 'number'
                    ? formatCurrency(String(value))
                    : unitType === '$' && typeof value == 'object' && value.length === 2
                    ? `${formatCurrency(String(value[0]))} - ${
                        exchangeRates[currency].symbol
                      }${formatCurrency(String(value[1]))}`
                    : value}
                  {unitType === '%' && typeof value == 'number' && '%'}
                </Typography>
              ) : (
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: COLOR_TEXT_1 }}>
                  {' '}
                  -{' '}
                </Typography>
              )}
              {isEditable && (
                <EditButton
                  isDisabled={!isUserReportOwner}
                  actionOnClick={() => {
                    if (editMode === 'modal') {
                      handleEditModalOpen();
                    } else if (editMode === 'scroll') {
                      setInLineEditToggled(true);
                    } else {
                      console.error('Edit mode not set');
                    }
                  }}
                  isLocked={false}
                />
              )}
            </Grid>
          ) : (
            <Grid container item xs={12} direction="column">
              <Grid
                item
                xs={12}
                container
                direction={'row'}
                justifyContent={'space-between'}
                alignItems={'center'}
                sx={{ marginBottom: '10px' }}
              >
                <Grid item xs={7}>
                  <InputTextField
                    field={{ key: kpiName, label: kpiName, unit: unitType }}
                    stateObj={{ [kpiName]: editValue }}
                    handleValueChange={(_, value) => {
                      setEditValue(value);
                    }}
                    currency={currency}
                    disabled={false}
                    hideLabel={true}
                  />
                </Grid>
                <Grid container item xs={4} direction="row" gap={1} justifyContent="flex-end">
                  <CustomIconButton
                    actionOnClick={() => setInLineEditToggled(false)}
                    Icon={<CloseIcon />}
                    title="Cancel"
                    backgroundColor={COLOR_GREY_200}
                    iconColor={COLOR_GREY_600}
                    hoverBackgroundColor="#222"
                    hoverIconColor="white"
                  />
                  <CustomIconButton
                    actionOnClick={() => {
                      hanldeKpiEdit(kpiKey, editValue);
                      setInLineEditToggled(false);
                    }}
                    Icon={<DoneRoundedIcon />}
                    title="Apply"
                    backgroundColor={COLOR_PRIMARY}
                    iconColor="white"
                  />
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Slider
                  size="medium"
                  value={editValue}
                  min={inLineEditConfig?.min}
                  max={inLineEditConfig?.max}
                  marks={inLineEditConfig?.marks
                    .reverse()
                    .map((mark, index, array) => {
                      if (index > 0) {
                        const prevMark = array[index - 1];
                        const isTooClose =
                          Math.abs((mark.value - prevMark.value) / mark.value) < 0.05;
                        return { ...mark, isLabelHidden: isTooClose };
                      }
                      return { ...mark, isLabelHidden: false };
                    })
                    .map(mark => ({
                      ...mark,
                      label: (
                        <Typography
                          variant="caption"
                          sx={{
                            color: COLOR_GREY_600,
                            display: mark.isLabelHidden ? 'none' : 'block',
                          }}
                        >
                          {mark.label}
                        </Typography>
                      ),
                    }))}
                  onChange={(event, value) => {
                    setEditValue(value as number);
                  }}
                  valueLabelDisplay="off"
                  sx={{
                    color: COLOR_PRIMARY,
                    ...styles.slider,
                  }}
                />
              </Grid>
            </Grid>
          )}
          {showEditedBadge && editedMetrics?.includes(kpiKey) && (
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
              <Chip label="Custom" color="primary" variant="outlined" size="small" />
            </Grid>
          )}
        </>
      </Grid>
    </Grid>
  );
};

export default KPICard;
